import * as React from 'react';

/**
 * Context holding the current partner's feature flags as a key→boolean map.
 * Populated by the FeatureFlagProvider in @artic/api-client.
 */
export const FeatureFlagsContext = React.createContext<Record<
  string,
  boolean
> | null>(null);

/**
 * Returns whether the specified feature flag is enabled for the current partner.
 * Returns `false` for unknown flags (safe default).
 *
 * Must be used within a `<FeatureFlagProvider>`.
 */
export function useFeatureFlag(flag: string): boolean {
  const ctx = React.useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error(
      'useFeatureFlag must be used within a <FeatureFlagProvider>',
    );
  }
  return ctx[flag] ?? false;
}

/**
 * Renders children only when the specified feature flag is enabled.
 * Optionally renders a fallback when disabled.
 */
export function FeatureFlag({
  flag,
  children,
  fallback = null,
}: {
  flag: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const enabled = useFeatureFlag(flag);
  return <>{enabled ? children : fallback}</>;
}
